
import React, { useState, useEffect } from 'react';
import { useVersionCheck } from '../../hooks/useVersionCheck';
import { RefreshCw, Rocket } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button';
import { useScrollLock } from '../../hooks/useScrollLock';

import { logger } from '@deskbay/shared';

export const VersionUpdater: React.FC = () => {
    const { isUpdateAvailable, reloadApp } = useVersionCheck();
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [progress, setProgress] = useState(0);

    const handleLater = () => {
        setIsVisible(false);
    };

    // Use centralized scroll lock
    useScrollLock(isVisible, handleLater);

    // Show modal when update is detected
    useEffect(() => {
        if (isUpdateAvailable) {
            setIsVisible(true);
        }
    }, [isUpdateAvailable]);

    const handleUpdate = async () => {
        setIsUpdating(true);
        setProgress(0);

        // Adatta timing basato sulla velocità di connessione
        const connection = (navigator as unknown as { connection?: { effectiveType?: string } }).connection;
        const connectionSpeed = connection?.effectiveType || '4g';
        const speedMultiplier = {
            'slow-2g': 3,
            '2g': 2.5,
            '3g': 1.5,
            '4g': 1
        }[connectionSpeed] || 1;

        const delay = (baseMs: number) => baseMs * speedMultiplier;

        // FASE 1: Preparazione aggiornamento (10%)
        setProgress(10);
        await new Promise(resolve => setTimeout(resolve, delay(300)));

        // FASE 2: Pulizia cache e storage (25%)
        setProgress(25);
        await new Promise(resolve => setTimeout(resolve, delay(500)));

        // FASE 3: Verifica nuova versione (40%)
        try {
            // Controllo reale della versione
            const response = await fetch(`/version.json?t=${new Date().getTime()}`, {
                cache: 'no-store',
            });
            if (response.ok) {
                logger.api.info('Version verified successfully', 'VersionUpdater');
            }
        } catch (error) {
            logger.api.info('Version check failed, proceeding anyway', 'VersionUpdater');
        }
        setProgress(40);
        await new Promise(resolve => setTimeout(resolve, delay(400)));

        // FASE 4: Pre-caricamento risorse critiche (60%)
        setProgress(60);
        await new Promise(resolve => setTimeout(resolve, delay(600)));

        // FASE 5: Salvataggio stato corrente (75%)
        // Simula salvataggio dati utente in localStorage/sessionStorage
        try {
            const currentState = {
                timestamp: Date.now(),
                url: window.location.href,
                userAgent: navigator.userAgent
            };
            sessionStorage.setItem('lastUpdateState', JSON.stringify(currentState));
        } catch (error) {
            logger.api.info('Could not save state', 'VersionUpdater');
        }
        setProgress(75);
        await new Promise(resolve => setTimeout(resolve, delay(300)));

        // FASE 6: Preparazione reload (85%)
        setProgress(85);
        await new Promise(resolve => setTimeout(resolve, delay(200)));

        // FASE 7: Esecuzione reload (95%)
        setProgress(95);
        await new Promise(resolve => setTimeout(resolve, delay(100)));

        // FASE 8: Completamento (100%)
        setProgress(100);
        await new Promise(resolve => setTimeout(resolve, delay(100)));

        // Ricarica l'applicazione
        reloadApp();
    };

    if (!isVisible) return null;

    return (
        <div className="fixed inset-0 bg-black/70 dark:bg-black/80 z-[9999] flex items-center justify-center p-4 sm:p-6 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl shadow-slate-900/10 dark:shadow-black/40 w-full max-w-md sm:max-w-lg overflow-hidden animate-scale-in border border-slate-100/60 dark:border-slate-800/70">
                <div className="p-4 sm:p-6 flex flex-col items-center text-center">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/40 sm:mx-0 sm:h-10 sm:w-10">
                        <Rocket className="h-5 w-5 text-sky-600 dark:text-sky-400" aria-hidden="true" />
                    </div>
                    <div className="mt-4 flex-1 text-center w-full">
                        <h3 className="text-lg sm:text-xl font-title font-semibold text-slate-900 dark:text-white">
                            {isUpdating
                                ? t('update_modal.updating_title', 'Aggiornamento in corso...')
                                : t('update_modal.title', 'Aggiornamento Disponibile')
                            }
                        </h3>
                        <div className="mt-2">
                            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                                {isUpdating
                                    ? t('update_modal.updating_description', 'Stiamo aggiornando Deskbay con le ultime funzionalità...')
                                    : t('update_modal.description', 'È disponibile una nuova versione di Deskbay. Aggiorna ora per accedere alle ultime funzionalità.')
                                }
                            </p>
                        </div>

                        {isUpdating && (
                            <div className="mt-6 sm:mt-8 flex flex-col items-center justify-center w-full">
                                <div className="relative w-24 h-24 sm:w-32 sm:h-32 flex items-center justify-center">
                                    {/* Cerchio di sfondo più sottile e discreto */}
                                    <svg className="absolute inset-0 w-full h-full transform -rotate-90">
                                        <circle
                                            cx={window.innerWidth >= 640 ? "64" : "48"}
                                            cy={window.innerWidth >= 640 ? "64" : "48"}
                                            r={window.innerWidth >= 640 ? "56" : "40"}
                                            stroke={window.matchMedia('(prefers-color-scheme: dark)').matches ? '#1e293b' : '#e2e8f0'}
                                            strokeWidth="4"
                                            fill="none"
                                            opacity="0.3"
                                        />
                                        {/* Cerchio di progresso con gradiente Deskbay */}
                                        <circle
                                            cx={window.innerWidth >= 640 ? "64" : "48"}
                                            cy={window.innerWidth >= 640 ? "64" : "48"}
                                            r={window.innerWidth >= 640 ? "56" : "40"}
                                            stroke="url(#deskbayGradient)"
                                            strokeWidth="4"
                                            fill="none"
                                            strokeLinecap="round"
                                            strokeDasharray={`${2 * Math.PI * (window.innerWidth >= 640 ? 56 : 40)}`}
                                            strokeDashoffset={`${2 * Math.PI * (window.innerWidth >= 640 ? 56 : 40) * (1 - progress / 100)}`}
                                            className="transition-all duration-700 ease-out"
                                        />
                                        <defs>
                                            <linearGradient id="deskbayGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                                <stop offset="0%" stopColor="#38bdf8" /> {/* sky-400 */}
                                                <stop offset="100%" stopColor="#6366f1" /> {/* indigo-500 */}
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                    {/* Percentuale al centro, dimensione ridotta ed elegante */}
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <span className="text-base sm:text-lg font-bold text-slate-700 dark:text-white font-title tracking-tight">
                                            {progress}%
                                        </span>
                                    </div>
                                </div>
                                <p className="mt-4 sm:mt-6 text-sm font-medium text-slate-600 dark:text-slate-300 font-title text-center">
                                    {t('update_modal.progress_label', 'Aggiornamento in corso...')}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
                <div className="bg-slate-50 dark:bg-slate-900/80 px-4 sm:px-6 py-3 sm:py-4 grid grid-cols-2 gap-2 sm:gap-3 border-t border-slate-100 dark:border-slate-800/70">
                    <Button
                        onClick={handleLater}
                        variant="secondary"
                        className="w-full text-sm sm:text-base"
                        disabled={isUpdating}
                    >
                        {t('update_modal.action_later', 'Più tardi')}
                    </Button>
                    <Button
                        onClick={handleUpdate}
                        variant="primary"
                        className="w-full gap-2 text-sm sm:text-base"
                        disabled={isUpdating}
                    >
                        {isUpdating ? (
                            <>
                                <RefreshCw className="w-4 h-4 animate-spin" />
                                {t('update_modal.action_updating', 'Aggiornamento...')}
                            </>
                        ) : (
                            <>
                                <RefreshCw className="w-4 h-4" />
                                {t('update_modal.action_update', 'Aggiorna Ora')}
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};
